import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  fetchSkyleadOverview,
  fetchSkyleadCampaigns,
  fetchSkyleadTimeline,
} from "@/lib/api";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";
import { Linkedin, Users, ArrowUpFromLine, MessageSquare, Mail, CheckCircle2, AlertCircle } from "lucide-react";

const fmt = (n) => Number(n || 0).toLocaleString("es-AR");
const pct = (n) => `${Number(n || 0).toFixed(1)}%`;
const fmtDate = (s) => {
  if (!s) return "";
  const d = new Date(s);
  return d.toLocaleDateString("es-AR", { day: "2-digit", month: "short" });
};

export default function OutboundSkyleadSection({ clientId, hasAccounts }) {
  const [days, setDays] = useState(30);
  const overviewQ = useQuery({
    queryKey: ["sk-overview", clientId],
    queryFn: () => fetchSkyleadOverview(clientId),
    enabled: hasAccounts,
    staleTime: 60_000,
  });
  const campaignsQ = useQuery({
    queryKey: ["sk-campaigns", clientId],
    queryFn: () => fetchSkyleadCampaigns(clientId),
    enabled: hasAccounts,
    staleTime: 60_000,
  });
  const timelineQ = useQuery({
    queryKey: ["sk-timeline", clientId, days],
    queryFn: () => fetchSkyleadTimeline(clientId, days),
    enabled: hasAccounts,
    staleTime: 60_000,
  });

  if (!hasAccounts) {
    return (
      <div className="bg-white border border-slate-200 rounded-md p-6 text-center" data-testid="bd-outbound-empty">
        <Linkedin className="h-8 w-8 mx-auto mb-2 text-slate-400" />
        <h3 className="font-display text-sm font-semibold text-slate-900 mb-1">Sin perfiles outreach configurados</h3>
        <p className="text-xs text-slate-500">Abrí <span className="font-medium">Configurar compromiso</span> y asociá uno o más perfiles LinkedIn al cliente.</p>
      </div>
    );
  }

  const totals = overviewQ.data?.totals || {};
  const profiles = overviewQ.data?.profiles || [];
  const profileCampaigns = campaignsQ.data?.profiles || [];
  const timeline = timelineQ.data || { invites: [], emails: [], totals: {} };

  return (
    <div className="space-y-5" data-testid="bd-outbound-skylead">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Outbound · plataforma outreach</p>
          <h3 className="font-display text-base font-semibold text-slate-900">{profiles.length} perfil{profiles.length === 1 ? "" : "es"} LinkedIn conectado{profiles.length === 1 ? "" : "s"}</h3>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="h-9 text-xs border border-slate-200 rounded-md px-2 bg-white"
            data-testid="sk-range"
          >
            <option value={7}>Últimos 7 días</option>
            <option value={14}>Últimos 14 días</option>
            <option value={30}>Últimos 30 días</option>
            <option value={60}>Últimos 60 días</option>
            <option value={90}>Últimos 90 días</option>
          </select>
        </div>
      </div>

      {/* KPI strip */}
      {overviewQ.isLoading ? (
        <div className="h-28 rounded-md skeleton" />
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="sk-kpis">
          <Kpi label="Nuevos contactos" sub="conexiones aceptadas" value={fmt(totals.connections_accepted)} tone="emerald" icon={Users} />
          <Kpi label="Respuestas totales" sub="message replies" value={fmt(totals.connection_replies)} tone="indigo" icon={MessageSquare} />
          <Kpi label="Invitaciones enviadas" value={fmt(totals.connections_requested)} sub={`acept. ${pct(totals.acceptance_rate)}`} tone="sky" icon={ArrowUpFromLine} />
          <Kpi label="Campañas activas" value={`${totals.active_campaigns || 0} / ${totals.total_campaigns || 0}`} sub={`${fmt(totals.total_leads)} leads en total`} tone="violet" icon={CheckCircle2} />
        </div>
      )}

      {/* Profiles row */}
      {profiles.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="sk-profiles">
          {profiles.map((p) => (
            <div key={p.account_id} className="bg-white border border-slate-200 rounded-md p-3">
              <div className="flex items-center gap-2 mb-2">
                <Linkedin className="h-3.5 w-3.5 text-[#0A66C2]" />
                <span className="font-medium text-xs text-slate-900 truncate">{p.name}</span>
              </div>
              <div className="grid grid-cols-3 gap-2 text-[11px]">
                <Mini label="Invites" value={fmt(p.connections_requested)} />
                <Mini label="Acept." value={fmt(p.connections_accepted)} />
                <Mini label="Replies" value={fmt(p.connection_replies)} />
                <Mini label="Msgs" value={fmt(p.messages_sent)} />
                <Mini label="Emails" value={fmt(p.emails_sent)} />
                <Mini label="Camp." value={`${p.active_campaign_count}/${p.campaign_count}`} />
              </div>
              {p.error && (
                <p className="text-[10px] text-red-600 mt-2 flex items-center gap-1"><AlertCircle className="h-3 w-3" /> {p.error}</p>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard
          title="Invitaciones, aceptaciones y respuestas"
          subtitle={`Total invites: ${fmt(timeline.totals.invitations_sent)} · aceptadas: ${fmt(timeline.totals.invitations_accepted)} · replies: ${fmt(timeline.totals.message_replies)}`}
          loading={timelineQ.isLoading}
          testid="sk-chart-invites"
        >
          <LineChart data={timeline.invites || []}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="date" tickFormatter={fmtDate} fontSize={11} stroke="#64748b" />
            <YAxis fontSize={11} stroke="#64748b" />
            <Tooltip />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Line type="monotone" dataKey="sent" name="Enviadas" stroke="#0EA5E9" strokeWidth={2} dot={false} />
            <Line type="monotone" dataKey="accepted" name="Aceptadas" stroke="#10B981" strokeWidth={2} dot={false} />
            <Line type="monotone" dataKey="replied" name="Respuestas" stroke="#8B5CF6" strokeWidth={2} dot={false} />
          </LineChart>
        </ChartCard>
        <ChartCard
          title="Emails: descubiertos, enviados y abiertos"
          subtitle={`Verificados: ${fmt(timeline.totals.emails_verified)} · enviados: ${fmt(timeline.totals.emails_sent)} · abiertos: ${fmt(timeline.totals.emails_opened)}`}
          loading={timelineQ.isLoading}
          testid="sk-chart-emails"
        >
          <LineChart data={timeline.emails || []}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="date" tickFormatter={fmtDate} fontSize={11} stroke="#64748b" />
            <YAxis fontSize={11} stroke="#64748b" />
            <Tooltip />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Line type="monotone" dataKey="verified" name="Descubiertos" stroke="#F59E0B" strokeWidth={2} dot={false} />
            <Line type="monotone" dataKey="sent" name="Enviados" stroke="#0EA5E9" strokeWidth={2} dot={false} />
            <Line type="monotone" dataKey="opened" name="Abiertos" stroke="#10B981" strokeWidth={2} dot={false} />
          </LineChart>
        </ChartCard>
      </div>

      {/* Campaigns table */}
      <CampaignsTable profiles={profileCampaigns} loading={campaignsQ.isLoading} />
    </div>
  );
}

function Kpi({ label, sub, value, tone, icon: Icon }) {
  const toneMap = {
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    indigo: "bg-indigo-50 text-indigo-700 border-indigo-200",
    sky: "bg-sky-50 text-sky-700 border-sky-200",
    violet: "bg-violet-50 text-violet-700 border-violet-200",
  }[tone] || "bg-slate-50 text-slate-700 border-slate-200";
  return (
    <div className={`border rounded-md p-3 ${toneMap}`}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-semibold mb-1">
        <Icon className="h-3 w-3" /> {label}
      </div>
      <div className="text-xl font-semibold text-slate-900 leading-tight">{value}</div>
      {sub && <div className="text-[10px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function Mini({ label, value }) {
  return (
    <div>
      <div className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
      <div className="text-xs font-semibold text-slate-900">{value}</div>
    </div>
  );
}

function ChartCard({ title, subtitle, loading, children, testid }) {
  return (
    <section className="bg-white border border-slate-200 rounded-md p-4" data-testid={testid}>
      <div className="mb-3">
        <h4 className="font-display text-sm font-semibold text-slate-900">{title}</h4>
        {subtitle && <p className="text-[10px] text-slate-500 mt-0.5">{subtitle}</p>}
      </div>
      <div className="h-64">
        {loading ? (
          <div className="h-full skeleton rounded-md" />
        ) : (
          <ResponsiveContainer width="100%" height="100%">{children}</ResponsiveContainer>
        )}
      </div>
    </section>
  );
}

function CampaignsTable({ profiles, loading }) {
  const [sort, setSort] = useState({ key: "total_leads", dir: "desc" });
  const flat = (profiles || []).flatMap((p) =>
    (p.campaigns || []).map((c) => ({ ...c, profile_name: p.profile?.name || "" }))
  );
  flat.sort((a, b) => {
    const av = a[sort.key];
    const bv = b[sort.key];
    if (typeof av === "string") return sort.dir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
    return sort.dir === "asc" ? (av || 0) - (bv || 0) : (bv || 0) - (av || 0);
  });
  const setSortKey = (key) => setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "desc" }));

  if (loading) return <div className="h-72 rounded-md skeleton" />;

  return (
    <section className="bg-white border border-slate-200 rounded-md p-5 min-w-0 max-w-full overflow-hidden" data-testid="sk-campaigns-table">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <h3 className="font-display text-sm font-semibold text-slate-900">Campañas activas</h3>
          <p className="text-[11px] text-slate-500 mt-0.5">{flat.length} en total · click columna para ordenar</p>
        </div>
      </div>
      <div className="overflow-x-auto -mx-5 max-h-[600px]">
        <table className="w-full text-[11px] min-w-[1100px]">
          <thead className="sticky top-0 z-10 bg-slate-50 border-b border-slate-200">
            <tr>
              <Th>Estado</Th>
              <Th onClick={() => setSortKey("name")} sortable>Campaña</Th>
              <Th>Perfil</Th>
              <Th onClick={() => setSortKey("total_leads")} sortable className="text-right">Total leads</Th>
              <Th onClick={() => setSortKey("remaining_leads")} sortable className="text-right">Pendientes</Th>
              <Th onClick={() => setSortKey("connections_requested")} sortable className="text-right">Invites</Th>
              <Th onClick={() => setSortKey("connections_accepted")} sortable className="text-right">Aceptadas</Th>
              <Th onClick={() => setSortKey("acceptance_rate")} sortable className="text-right">% Acep.</Th>
              <Th onClick={() => setSortKey("messages_sent")} sortable className="text-right">Msgs</Th>
              <Th onClick={() => setSortKey("connection_replies")} sortable className="text-right">Replies</Th>
              <Th onClick={() => setSortKey("response_rate")} sortable className="text-right">% Reply</Th>
              <Th onClick={() => setSortKey("emails_sent")} sortable className="text-right">Emails env.</Th>
              <Th onClick={() => setSortKey("emails_verified")} sortable className="text-right">Verif.</Th>
            </tr>
          </thead>
          <tbody>
            {flat.map((c) => (
              <tr key={`${c.campaign_id}-${c.profile_name}`} className="border-b border-slate-100 hover:bg-slate-50">
                <Td>
                  <span className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded-sm ${
                    c.is_active ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-slate-50 text-slate-500 border border-slate-200"
                  }`}>
                    <span className={`h-1.5 w-1.5 rounded-full ${c.is_active ? "bg-emerald-500" : "bg-slate-300"}`} />
                    {c.is_active ? "Activa" : "Pausa"}
                  </span>
                </Td>
                <Td className="text-slate-900 font-medium max-w-[280px] truncate" title={c.name}>{c.name || "—"}</Td>
                <Td className="text-slate-600">{c.profile_name}</Td>
                <Td className="text-right tabular-nums">{fmt(c.total_leads)}</Td>
                <Td className="text-right tabular-nums text-slate-500">{fmt(c.remaining_leads)}</Td>
                <Td className="text-right tabular-nums">{fmt(c.connections_requested)}</Td>
                <Td className="text-right tabular-nums">{fmt(c.connections_accepted)}</Td>
                <Td className="text-right tabular-nums text-emerald-700 font-medium">{pct(c.acceptance_rate)}</Td>
                <Td className="text-right tabular-nums">{fmt(c.messages_sent)}</Td>
                <Td className="text-right tabular-nums">{fmt(c.connection_replies)}</Td>
                <Td className="text-right tabular-nums text-indigo-700 font-medium">{pct(c.response_rate)}</Td>
                <Td className="text-right tabular-nums">{fmt(c.emails_sent)}</Td>
                <Td className="text-right tabular-nums text-slate-500">{fmt(c.emails_verified)}</Td>
              </tr>
            ))}
            {flat.length === 0 && (
              <tr><td colSpan={13} className="text-center text-xs text-slate-500 py-6">Sin campañas para mostrar.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function Th({ children, sortable, onClick, className = "" }) {
  return (
    <th
      onClick={onClick}
      className={`px-2 py-2 text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold ${sortable ? "cursor-pointer hover:text-slate-900" : ""} ${className}`}
    >
      {children}
    </th>
  );
}
function Td({ children, className = "", title }) {
  return <td className={`px-2 py-2 align-middle text-slate-700 ${className}`} title={title}>{children}</td>;
}
